import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Container,
  Stack,
  Divider,
  Chip,
  Alert,
  Grid,
  InputAdornment,
  alpha,
  Switch,
  FormControlLabel,
  Tooltip,
  Checkbox,
} from "@mui/material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import html2pdf from 'html2pdf.js';

// Icons
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import PreviewIcon from "@mui/icons-material/Preview";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DraftsIcon from "@mui/icons-material/Drafts";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import InventoryIcon from "@mui/icons-material/Inventory";
import PaymentIcon from "@mui/icons-material/Payment";
import DiscountIcon from "@mui/icons-material/Discount";
import NotesIcon from "@mui/icons-material/Notes";
import CalculateIcon from "@mui/icons-material/Calculate";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

// Services
import * as saleOrderService from "../services/saleOrderService";
import { generateSaleOrderPdfHtml, generateAndPreviewSaleOrderPDF } from "../services/saleOrderPdfService";
import * as vatService from "../../companysettings/services/vatService";

// Components
import CustomerSelect from "../../invoices/components/CustomerSelect";
import ProductSelect from "../../invoices/components/ProductSelect";
import AddCustomerDialog from "../../customers/components/AddCustomerDialog";
import AddProductDialog from "../../product/components/AddProductDialog";
import ConfirmationDialog from "../../../components/ConfirmationDialog";
import InvoicePreviewDialog from "../../../components/InvoicePreviewDialog";

// Shared Document Components
import {
  DocumentHeaderSection,
  DocumentDetailsBar,
  DocumentProductsTable,
  DocumentNotesSection,
  DocumentTotalsCard,
} from '../../../components/shared';

// Hooks
import useStoreInfo from '../../../hooks/useCompanyInfo';
import { searchCustomers, getCustomerOutstandingDocuments } from '../../customers/services/customerService';
import { searchProducts } from '../../product/services/productService';

// Constants
const MAX_CUSTOM_TEXT_LENGTH = 1000;

// Generate sale order number
function generateSaleOrderNumber() {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yy = String(now.getFullYear()).slice(-2);
  const rand = String(Math.floor(1000 + Math.random() * 9000));
  return `SO-${dd}${mm}${yy}${rand}`;
}

function AddSaleOrderPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { storeInfo } = useStoreInfo();
  const reduxLanguage = useSelector((state) => state.settings?.language);
  const language = reduxLanguage || i18n.language || 'en';
  
  // Get sale order ID from URL params (for reopen/continue) - memoized to prevent unnecessary re-renders
  const saleOrderIdFromUrl = useMemo(() => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('id');
  }, [location.search]);
  
  // Check if this is a reopen action
  const isReopenAction = useMemo(() => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('reopen') === 'true';
  }, [location.search]);
  
  // Ref to store sale order ID immediately (synchronous)
  const saleOrderIdRef = useRef(null);
  
  // Ref to track if initial data is loaded (to prevent auto-save on mount)
  const isInitialLoadRef = useRef(true);
  const quantityUpdateTimerRef = useRef({});
  const priceInputRefs = useRef({});
  const lastRequestedSaleOrderIdRef = useRef(null); // Track last requested ID to prevent duplicate API calls
  
  // Ref to track original values to prevent unnecessary auto-save calls
  const originalValuesRef = useRef({});
  
  // Ref to track last saved state to prevent unnecessary auto-save API calls
  const lastSavedStateRef = useRef({
    customText: '',
    discountValue: 0,
    discountType: 'percentage',
    vatPercentage: 0,
    isTvaIncluded: true,
    // Add other fields as needed
  });
  
  // Main state
  const [saleOrderId, setSaleOrderId] = useState(null);
  const [saleOrderNumber, setSaleOrderNumber] = useState("");
  const [saleOrderDate, setSaleOrderDate] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const selectedCustomerRef = React.useRef(null);
  // Keep ref in sync so debounced/async callbacks always read the latest customer
  React.useEffect(() => {
    selectedCustomerRef.current = selectedCustomer;
  }, [selectedCustomer]);
  const [customerChangeInProgress, setCustomerChangeInProgress] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [price, setPrice] = useState("");
  const [customText, setCustomText] = useState("");
  const [saleOrderProducts, setSaleOrderProducts] = useState([]);
  const [vatPercentage, setVatPercentage] = useState(0);
  const [vatRates, setVatRates] = useState([]);
  const [status, setStatus] = useState('Draft'); // 'Draft' or 'Closed'
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [openCustomerDialog, setOpenCustomerDialog] = useState(false);
  const [openEditCustomerDialog, setOpenEditCustomerDialog] = useState(false);
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [showProfit, setShowProfit] = useState(false);
  const [productSearchText, setProductSearchText] = useState("");
  const [pendingProductForTable, setPendingProductForTable] = useState(null);
  
  // Error states
  const [generalErrors, setGeneralErrors] = useState([]);
  const [customerError, setCustomerError] = useState("");
  const [productError, setProductError] = useState("");
  
  // Confirmation dialogs
  const [saveConfirmDialog, setSaveConfirmDialog] = useState(false);
  const [draftConfirmDialog, setDraftConfirmDialog] = useState(false);
  const [stockWarningDialog, setStockWarningDialog] = useState(false);
  const [stockWarningData, setStockWarningData] = useState(null);
  
  // Payment & Discount state
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [useMultiplePayments, setUseMultiplePayments] = useState(false);
  const [discountType, setDiscountType] = useState("percentage");
  const [discountValue, setDiscountValue] = useState(0);
  const [invoicePaidAmount, setInvoicePaidAmount] = useState("");
  const [isTvaIncluded, setIsTvaIncluded] = useState(true);

  // Outstanding invoices
  const [outstandingInvoices, setOutstandingInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [showOutstanding, setShowOutstanding] = useState(false);

  // Shared helper: populate UI state from a sale order object (used by both load and draft-found paths)
  const populateSaleOrderData = (existingSO, skipCustomer = false) => {
    // Block auto-save while populating data (it may have been set to false already)
    isInitialLoadRef.current = true;
    
    setSaleOrderId(existingSO.Id);
    saleOrderIdRef.current = existingSO.Id;
    setSaleOrderNumber(existingSO.SaleOrderNumber);
    
    // Fix date formatting - handle date string properly
    if (existingSO.SaleOrderDate && existingSO.SaleOrderDate !== '0001-01-01T00:00:00') {
      const dateStr = existingSO.SaleOrderDate.split('T')[0];
      if (dateStr && dateStr !== '0001-01-01') {
        setSaleOrderDate(dateStr);
      } else {
        const today = new Date();
        setSaleOrderDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`);
      }
    } else {
      const today = new Date();
      setSaleOrderDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`);
    }
    
    // If this is a reopen action, always set status to Draft
    setStatus(isReopenAction ? 'Draft' : (existingSO.Status || 'Draft'));
    setVatPercentage(existingSO.VATPercentage || 0);
    setCustomText(existingSO.CustomText || '');
    
    // Load discount and payment data
    // Map DiscountType from backend: 'Percentage' → 'percentage', 'Fixed' → 'fixed'
    let loadedDiscountType = 'percentage';
    let loadedDiscountValue = 0;
    
    if (existingSO.DiscountType) {
      // Use DiscountType if available (new format)
      loadedDiscountType = existingSO.DiscountType.toLowerCase() === 'percentage' ? 'percentage' : 'fixed';
      loadedDiscountValue = loadedDiscountType === 'percentage' 
        ? (existingSO.DiscountPercentage || 0) 
        : (existingSO.DiscountAmount || 0);
      setDiscountType(loadedDiscountType);
      setDiscountValue(loadedDiscountValue);
    } else if (existingSO.DiscountPercentage > 0) {
      // Fallback to old format for backward compatibility
      loadedDiscountType = 'percentage';
      loadedDiscountValue = existingSO.DiscountPercentage;
      setDiscountType('percentage');
      setDiscountValue(existingSO.DiscountPercentage);
    } else if (existingSO.DiscountAmount > 0) {
      loadedDiscountType = 'fixed';
      loadedDiscountValue = existingSO.DiscountAmount;
      setDiscountType('fixed');
      setDiscountValue(existingSO.DiscountAmount);
    } else {
      setDiscountType('percentage');
      setDiscountValue(0);
    }
    
    // Update last saved state with loaded values
    lastSavedStateRef.current = {
      customText: existingSO.CustomText || '',
      discountValue: loadedDiscountValue,
      discountType: loadedDiscountType,
      vatPercentage: Number(existingSO.VATPercentage) || 0,
      isTvaIncluded: Boolean(existingSO.IsTvaIncluded ?? true),
    };
    
    setInvoicePaidAmount(existingSO.PaidAmount ? String(existingSO.PaidAmount) : '');
    setIsTvaIncluded(existingSO.IsTvaIncluded !== undefined ? existingSO.IsTvaIncluded : true);
    
    // Load customer (skip if already set, e.g. from prefilled navigation)
    if (!skipCustomer && existingSO.CustomerId) {
      const customerObj = existingSO.Customer || {};
      const customerData = {
        Id: existingSO.CustomerId,
        CompanyName: existingSO.CustomerName,
        Name: existingSO.CustomerName,
        Street: customerObj.Street || '',
        City: customerObj.City || '',
        PostCode: customerObj.PostCode || '',
        Address: (customerObj.Street || '') + ' ' + (customerObj.City || '') + ' ' + (customerObj.PostCode || ''),
        VATNumber: customerObj.VATNumber,
        Email: customerObj.Email || '',
        PhoneNumber: customerObj.PhoneNumber || customerObj.MobileNumber || '',
      };
      setSelectedCustomer(customerData);
    }
    
    // Load line items
    if (existingSO.LineItems && existingSO.LineItems.length > 0) {
      const loadedItems = existingSO.LineItems.map(item => ({
        ...item,
        _rowKey: Date.now() + Math.random(),
        Name: item.ProductName,
        Price: item.UnitPrice,
        SKU: item.SKU || null,
        BarCode: item.BarCode || null,
        SKUBarCode: item.SKUBarCode || null,
        CostPrice: item.CostPrice || 0,
        BuyPrice: item.CostPrice || 0,
        applyDiscount: item.applyDiscount !== false,
      }));
      setSaleOrderProducts(loadedItems);
    }
    
    // Mark initial load complete after a delay to prevent auto-save trigger
    setTimeout(() => {
      isInitialLoadRef.current = false;
    }, 1500);
  };

  // Initialize or load existing sale order
  useEffect(() => {
    const loadExistingSaleOrder = async () => {
      if (saleOrderIdFromUrl) {
        // Prevent duplicate loads - skip if we already requested or loaded this exact ID
        if (lastRequestedSaleOrderIdRef.current === saleOrderIdFromUrl) {
          return;
        }
        
        // Mark this ID as requested immediately (before async call)
        lastRequestedSaleOrderIdRef.current = saleOrderIdFromUrl;
        
        try {
          const response = await saleOrderService.getSaleOrderById(saleOrderIdFromUrl);
          const existingSO = response?.payload || response?.data || response;
          populateSaleOrderData(existingSO);
        } catch (error) {

          setGeneralErrors([error.message || t('failed_to_load_sale_order') || 'Failed to load sale order']);
          setSaleOrderNumber(generateSaleOrderNumber());
          isInitialLoadRef.current = false;
          // Reset so user can retry
          lastRequestedSaleOrderIdRef.current = null;
        }
      } else {
        setSaleOrderNumber(generateSaleOrderNumber());
        isInitialLoadRef.current = false;
      }
    };
    
    loadExistingSaleOrder();
  }, [saleOrderIdFromUrl]);

  // Fetch VAT rates on mount
  useEffect(() => {
    const fetchVatRatesData = async () => {
      try {
        const vatData = await vatService.getVatRates();
        const rates = vatData?.payload || vatData || [];
        setVatRates(rates);
      } catch (error) {

        // Use default rates if API fails
        setVatRates([]);
      }
    };
    fetchVatRatesData();
  }, []);

  // Handle pre-filled customer from navigation (e.g., from CustomerDetailPage)
  useEffect(() => {
    if (location.state?.prefilledCustomer && !saleOrderIdFromUrl) {
      const prefilledCustomer = location.state.prefilledCustomer;
      setSelectedCustomer(prefilledCustomer);
      
      // Clear the state to prevent re-population on component updates
      if (window.history.state) {
        window.history.replaceState({ ...window.history.state, usr: null }, '');
      }
    }
  }, [location.state, saleOrderIdFromUrl]);

  // Handle pre-filled data from Offer conversion
  useEffect(() => {
    if (!location.state?.fromOffer || saleOrderIdFromUrl) return;
    const { customer, lineItems, discountAmount, vatPercentage: offerVat, customText: offerNotes } = location.state.fromOffer;

    // Block auto-save while populating
    isInitialLoadRef.current = true;

    if (customer) {
      setSelectedCustomer({
        Id: customer.Id,
        CompanyName: customer.Name || customer.CompanyName || '',
        Name: customer.Name || customer.CompanyName || '',
        Street: customer.Street || '',
        City: customer.City || '',
        PostCode: customer.PostCode || '',
        VATNumber: customer.VATNumber || '',
        Email: customer.Email || '',
        PhoneNumber: customer.PhoneNumber || customer.MobilePhone || '',
      });
    }

    if (Array.isArray(lineItems) && lineItems.length > 0) {
      const mappedProducts = lineItems.map(item => ({
        ...item,
        _rowKey: Date.now() + Math.random(),
        Name: item.ProductName || item.Product?.Name || `Product #${item.ProductId}`,
        Price: item.UnitPrice,
        UnitPrice: item.UnitPrice,
        Quantity: item.Quantity,
        SKU: item.SKU || null,
        BarCode: item.BarCode || null,
        SKUBarCode: item.SKUBarCode || null,
        TotalExcl: item.TotalExcl ?? (Number(item.Quantity || 0) * Number(item.UnitPrice || 0)),
        CostPrice: item.CostPrice || 0,
        BuyPrice: item.CostPrice || 0,
        applyDiscount: true,
      }));
      setSaleOrderProducts(mappedProducts);
    }

    if (discountAmount > 0) {
      setDiscountType('fixed');
      setDiscountValue(discountAmount);
    }

    if (offerVat) setVatPercentage(offerVat);
    if (offerNotes) setCustomText(offerNotes);

    // Release auto-save block after state settles
    setTimeout(() => { isInitialLoadRef.current = false; }, 1500);

    // Clear state so refresh doesn’t re-populate
    window.history.replaceState({ ...window.history.state, usr: null }, '');
  }, [location.state, saleOrderIdFromUrl]);


  // Auto-set price when product is selected
  useEffect(() => {
    if (selectedProduct?.Price) {
      setPrice(String(selectedProduct.Price));
    }
  }, [selectedProduct]);

  // Recalculate all line items when VAT percentage changes
  useEffect(() => {
    // Skip recalculation during initial load
    if (isInitialLoadRef.current) {
      return;
    }
    
    if (saleOrderProducts.length > 0) {
      const updatedProducts = saleOrderProducts.map(product => {
        const quantity = parseFloat(product.Quantity) || 0;
        const unitPrice = parseFloat(product.UnitPrice) || 0;
        let totalExcl, vatAmount, totalIncl;

        if (isTvaIncluded) {
          // Prices include VAT
          totalIncl = quantity * unitPrice;
          totalExcl = totalIncl / (1 + vatPercentage / 100);
          vatAmount = totalIncl - totalExcl;
        } else {
          // Prices exclude VAT
          totalExcl = quantity * unitPrice;
          vatAmount = vatPercentage > 0 ? totalExcl * (vatPercentage / 100) : 0;
          totalIncl = totalExcl + vatAmount;
        }

        return {
          ...product,
          TotalExcl: totalExcl,
          VATAmount: vatAmount,
          TotalIncl: totalIncl,
        };
      });
      setSaleOrderProducts(updatedProducts);
    }
  }, [vatPercentage, isTvaIncluded]);

  // Create sale order when customer is selected (only for new sale orders, not when loading from URL)
  useEffect(() => {
    const createSaleOrderOnCustomerSelect = async () => {
      const currentSaleOrderId = saleOrderId || saleOrderIdRef.current;
      
      // Skip if we're loading from URL or if sale order already exists or if creation is in progress
      if (saleOrderIdFromUrl || !selectedCustomer || currentSaleOrderId || customerChangeInProgress) {
        return;
      }
      
      // Block auto-save during sale order creation
      isInitialLoadRef.current = true;
      
      setCustomerChangeInProgress(true);
      setCustomerError("");
      
      // Check if customer already has a draft sale order
      let existingDraft = null;
      try {
        const response = await saleOrderService.getDraftByCustomer(selectedCustomer.Id);
        existingDraft = response?.payload || response?.data || response;
      } catch (error) {
        // Error is expected when no draft exists (errorCode 26)
        const errorCode = error?.response?.data?.errorCode || error?.errorCode;
        if (errorCode !== 26) {

        }
        // Continue to create new sale order
      }
      
      // If draft exists, load it
      if (existingDraft && existingDraft.Id) {
        const draftId = existingDraft.Id || existingDraft.id;
        toast.info(t('opening_existing_draft_sale_order') || 'Opening existing draft sale order for this customer.');
        
        // Populate UI from draft data directly (skip customer since already selected)
        populateSaleOrderData(existingDraft, true);
        
        // Mark this ID so loadExistingSaleOrder won't re-fetch
        lastRequestedSaleOrderIdRef.current = String(draftId);
        
        // Update URL silently for bookmark/refresh persistence WITHOUT triggering React Router
        window.history.replaceState(null, '', `/sales/sale-orders/add?id=${draftId}`);
        setCustomerChangeInProgress(false);
        return;
      }
      
      // No draft exists - create new sale order
      try {
        const saleOrderData = {
          SaleOrderNumber: saleOrderNumber,
          SaleOrderDate: saleOrderDate,
          CustomerId: selectedCustomer.Id,
          CustomerName: selectedCustomer.CompanyName || selectedCustomer.Name,
          VATPercentage: vatPercentage,
          CustomText: customText,
          Status: 'Draft',
          PaymentStatus: 'Not Paid',
          PaidAmount: 0,
          TotalIncl: 0,
          IsTvaIncluded: isTvaIncluded,
          LineItems: [],
        };

        const createResponse = await saleOrderService.addSaleOrder(saleOrderData);
        const created = createResponse?.payload || createResponse?.data || createResponse;
        
        // Extract ID from response
        const createdId = created?.Id || created?.id || created;
        
        if (!createdId) {

          setCustomerError(t('failed_to_create_sale_order') || 'Failed to create sale order');
          setCustomerChangeInProgress(false);
          return;
        }
        
        // Set both state and ref immediately
        setSaleOrderId(createdId);
        saleOrderIdRef.current = createdId;
        
        // Initialize last saved state for new sale order
        lastSavedStateRef.current = {
          customText: customText || '',
          discountValue: parseFloat(discountValue) || 0,
          discountType: discountType,
          vatPercentage: Number(vatPercentage) || 0,
          isTvaIncluded: Boolean(isTvaIncluded),
        };
        
        // Mark this ID so loadExistingSaleOrder won't re-fetch when URL updates
        lastRequestedSaleOrderIdRef.current = String(createdId);
        
        // Update URL with sale order ID for page refresh persistence
        navigate(`/sales/sale-orders/add?id=${createdId}`, { replace: true });
        
        // Sale order created - no toast needed
        
        // Mark initial load complete after sale order creation
        // Delay must be longer than auto-save debounce (1000ms) to ensure no auto-save on creation
        setTimeout(() => {
          isInitialLoadRef.current = false;
        }, 1500);
      } catch (error) {

        setCustomerError(t('failed_to_create_sale_order') || 'Failed to create sale order');
        setSelectedCustomer(null);
      } finally {
        setCustomerChangeInProgress(false);
      }
    };

    createSaleOrderOnCustomerSelect();
  }, [selectedCustomer, saleOrderIdFromUrl]); // Only trigger when customer selection or URL param changes

  // Auto-save: Update sale order when key fields change (with debouncing)
  useEffect(() => {
    const autoSaveTimer = setTimeout(async () => {
      // Skip auto-save if initial load is not complete
      if (isInitialLoadRef.current) {
        return;
      }
      
      const urlParams = new URLSearchParams(window.location.search);
      const urlId = urlParams.get('id');
      const currentSaleOrderId = saleOrderId || saleOrderIdRef.current || urlId;
      
      // Only auto-save if:
      // 1. Sale order ID exists
      // 2. Customer change is not in progress
      // 3. Sale order is not closed (only save drafts)
      // 4. Customer and sale order number exist
      if (currentSaleOrderId && !customerChangeInProgress && status === 'Draft' && selectedCustomer && saleOrderNumber) {
        // Check if any values have actually changed from last save
        const currentCustomText = customText || '';
        const currentDiscountValue = parseFloat(discountValue) || 0;
        const currentDiscountType = discountType;
        const currentVatPercentage = Number(vatPercentage) || 0;
        const currentIsTvaIncluded = Boolean(isTvaIncluded);
        
        const hasChanged = 
          currentCustomText !== lastSavedStateRef.current.customText ||
          currentDiscountValue !== lastSavedStateRef.current.discountValue ||
          currentDiscountType !== lastSavedStateRef.current.discountType ||
          currentVatPercentage !== lastSavedStateRef.current.vatPercentage ||
          currentIsTvaIncluded !== lastSavedStateRef.current.isTvaIncluded;
        
        // Skip auto-save if nothing changed
        if (!hasChanged) {
          return;
        }
        
        try {
          // Calculate totals inline for auto-save
          const calcTotalExcl = saleOrderProducts.reduce((sum, p) => sum + Number(p.TotalExcl || 0), 0);
          const calcTotalIncl = saleOrderProducts.reduce((sum, p) => sum + Number(p.TotalIncl || 0), 0);
          
          // Calculate discount amount
          let calcDiscountAmount = 0;
          let calcSubtotalAfterDiscount = 0;
          let calcVatAfterDiscount = 0;
          let calcGrandTotal = 0;
          
          if (isTvaIncluded) {
            // Prices already include VAT
            if (vatPercentage > 0) {
              // When VAT included and rate not zero, apply discount on excl VAT amount
              const totalExclVAT = calcTotalIncl / (1 + vatPercentage / 100);
              if (discountValue > 0) {
                if (discountType === "percentage") {
                  calcDiscountAmount = Number(totalExclVAT * (discountValue / 100)) || 0;
                } else {
                  calcDiscountAmount = Number(discountValue) || 0;
                }
              }
              const subtotalExclAfterDiscount = totalExclVAT - calcDiscountAmount;
              calcVatAfterDiscount = subtotalExclAfterDiscount * (vatPercentage / 100);
              calcGrandTotal = subtotalExclAfterDiscount + calcVatAfterDiscount;
              calcSubtotalAfterDiscount = calcGrandTotal;
            } else {
              // VAT is 0, apply discount on total
              if (discountValue > 0) {
                if (discountType === "percentage") {
                  calcDiscountAmount = Number(calcTotalIncl * (discountValue / 100)) || 0;
                } else {
                  calcDiscountAmount = Number(discountValue) || 0;
                }
              }
              calcSubtotalAfterDiscount = calcTotalIncl - calcDiscountAmount;
              calcGrandTotal = calcSubtotalAfterDiscount;
              calcVatAfterDiscount = 0;
            }
          } else {
            // Prices exclude VAT - calculate VAT on subtotal after discount
            if (discountValue > 0) {
              if (discountType === "percentage") {
                calcDiscountAmount = Number(calcTotalExcl * (discountValue / 100)) || 0;
              } else {
                calcDiscountAmount = Number(discountValue) || 0;
              }
            }
            calcSubtotalAfterDiscount = calcTotalExcl - calcDiscountAmount;
            calcVatAfterDiscount = vatPercentage > 0 ? calcSubtotalAfterDiscount * (vatPercentage / 100) : 0;
            calcGrandTotal = calcSubtotalAfterDiscount + calcVatAfterDiscount;
          }
          
          const updateData = {
            SaleOrderNumber: saleOrderNumber,
            SaleOrderDate: saleOrderDate,
            CustomerId: selectedCustomer.Id,
            CustomerName: selectedCustomer.CompanyName || selectedCustomer.Name,
            CustomText: customText,
            VATPercentage: vatPercentage,
            Status: status,
            DiscountType: discountType === 'percentage' ? 'Percentage' : 'Fixed',
            DiscountPercentage: discountType === 'percentage' ? discountValue : null,
            DiscountAmount: discountType === 'fixed' ? discountValue : calcDiscountAmount,
            TotalExcl: calcTotalExcl,
            TotalVAT: calcVatAfterDiscount,
            TotalIncl: calcGrandTotal,
            IsTvaIncluded: isTvaIncluded,
            LineItems: saleOrderProducts.map((item) => ({
              Id: item.Id ?? item.LineItemId ?? undefined,
              ProductId: item.ProductId ?? null,
              ProductName: item.Name || item.ProductName || item.DisplayName || '',
              SKUBarCode: item.SKU || item.BarCode || item.SKUBarCode || null,
              Quantity: Number(item.Quantity || 1),
              UnitPrice: Number(item.UnitPrice || item.Price || 0),
              UnitPriceExclTVA: Number(item.UnitPriceExclTVA || 0),
              UnitPriceInclTVA: Number(item.UnitPriceInclTVA || item.UnitPrice || item.Price || 0),
              TotalExcl: Number(item.TotalExcl || 0),
              TotalIncl: Number(item.TotalIncl || 0),
              VATAmount: Number(item.VATAmount || item.TotalVAT || 0),
              CostPrice: Number(item.CostPrice || 0),
              SalePrice: Number(item.Price || item.UnitPrice || 0),
              VATPercentage: Number(item.VAT || 0),
            })),
          };
          await saleOrderService.updateSaleOrder(currentSaleOrderId, updateData);
          
          // Update last saved state after successful save
          lastSavedStateRef.current = {
            customText: currentCustomText,
            discountValue: currentDiscountValue,
            discountType: currentDiscountType,
            vatPercentage: currentVatPercentage,
            isTvaIncluded: currentIsTvaIncluded,
          };
          
          // Silent update - no toast notification for auto-save
        } catch (error) {

          // Silent failure - don't interrupt user
          // Only log if it's not a 404 (which means sale order doesn't exist yet)
          if (error?.response?.status !== 404) {

          }
        }
      }
    }, 1000); // Debounce for 1 second

    return () => clearTimeout(autoSaveTimer);
  }, [customText, vatPercentage, discountType, discountValue, saleOrderId, customerChangeInProgress, status, selectedCustomer, saleOrderNumber, saleOrderDate, saleOrderProducts, isTvaIncluded]);

  // Update sale order with latest totals after line item changes
  const updateSaleOrderTotals = async (products = saleOrderProducts) => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const urlId = urlParams.get('id');
      const currentSaleOrderId = saleOrderId || saleOrderIdRef.current || urlId;
      
      if (!currentSaleOrderId || !selectedCustomer || status !== 'Draft') {
        return; // Only update draft orders
      }

      // Calculate totals
      const calcTotalExcl = products.reduce((sum, p) => sum + Number(p.TotalExcl || 0), 0);
      const calcTotalIncl = products.reduce((sum, p) => sum + Number(p.TotalIncl || 0), 0);
      
      // Calculate discountable amounts
      const discountableExcl = products
        .filter(p => p.applyDiscount !== false)
        .reduce((sum, p) => sum + Number(p.TotalExcl || 0), 0);
      const discountableIncl = products
        .filter(p => p.applyDiscount !== false)
        .reduce((sum, p) => sum + Number(p.TotalIncl || 0), 0);
      
      // Calculate discount amount
      let calcDiscountAmount = 0;
      let calcSubtotalAfterDiscount = 0;
      let calcVatAfterDiscount = 0;
      let calcGrandTotal = 0;
      
      if (isTvaIncluded) {
        if (vatPercentage > 0) {
          // When VAT included and rate not zero, apply discount on excl VAT amount
          const totalExclVAT = calcTotalIncl / (1 + vatPercentage / 100);
          const discountableExclVAT = discountableIncl / (1 + vatPercentage / 100);
          if (discountValue > 0) {
            if (discountType === "percentage") {
              calcDiscountAmount = Number(discountableExclVAT * (discountValue / 100)) || 0;
            } else {
              calcDiscountAmount = Number(discountValue) || 0;
            }
          }
          const subtotalExclAfterDiscount = totalExclVAT - calcDiscountAmount;
          calcVatAfterDiscount = subtotalExclAfterDiscount * (vatPercentage / 100);
          calcGrandTotal = subtotalExclAfterDiscount + calcVatAfterDiscount;
          calcSubtotalAfterDiscount = calcGrandTotal;
        } else {
          // VAT is 0, apply discount on total
          if (discountValue > 0) {
            if (discountType === "percentage") {
              calcDiscountAmount = Number(discountableIncl * (discountValue / 100)) || 0;
            } else {
              calcDiscountAmount = Number(discountValue) || 0;
            }
          }
          calcSubtotalAfterDiscount = calcTotalIncl - calcDiscountAmount;
          calcGrandTotal = calcSubtotalAfterDiscount;
          calcVatAfterDiscount = 0;
        }
      } else {
        if (discountValue > 0) {
          if (discountType === "percentage") {
            calcDiscountAmount = Number(discountableExcl * (discountValue / 100)) || 0;
          } else {
            calcDiscountAmount = Number(discountValue) || 0;
          }
        }
        calcSubtotalAfterDiscount = calcTotalExcl - calcDiscountAmount;
        calcVatAfterDiscount = vatPercentage > 0 ? calcSubtotalAfterDiscount * (vatPercentage / 100) : 0;
        calcGrandTotal = calcSubtotalAfterDiscount + calcVatAfterDiscount;
      }
      
      // Calculate profit
      const totalCost = products.reduce((sum, p) => {
        const costPrice = Number(p.CostPrice || 0);
        const quantity = Number(p.Quantity || 0);
        return sum + (costPrice * quantity);
      }, 0);
      const actualSalesAmount = isTvaIncluded ? (calcSubtotalAfterDiscount / (1 + vatPercentage / 100)) : calcSubtotalAfterDiscount;
      const calcTotalProfit = actualSalesAmount - totalCost;
      
      const updateData = {
        SaleOrderNumber: saleOrderNumber,
        SaleOrderDate: saleOrderDate,
        CustomerId: selectedCustomer.Id,
        CustomerName: selectedCustomer.CompanyName || selectedCustomer.Name,
        CustomText: customText,
        VATPercentage: vatPercentage,
        Status: status,
        DiscountType: discountType === 'percentage' ? 'Percentage' : 'Fixed',
        DiscountPercentage: discountType === 'percentage' ? discountValue : null,
        DiscountAmount: discountType === 'fixed' ? discountValue : calcDiscountAmount,
        TotalExcl: calcTotalExcl,
        TotalVAT: calcVatAfterDiscount,
        TotalIncl: calcGrandTotal,
        IsTvaIncluded: isTvaIncluded,
        TotalProfit: calcTotalProfit,
        LineItems: products.map((item) => ({
          Id: item.Id ?? item.LineItemId ?? undefined,
          ProductId: item.ProductId ?? null,
          ProductName: item.Name || item.ProductName || item.DisplayName || '',
          SKUBarCode: item.SKU || item.BarCode || item.SKUBarCode || null,
          Quantity: Number(item.Quantity || 1),
          UnitPrice: Number(item.UnitPrice || item.Price || 0),
          UnitPriceExclTVA: Number(item.UnitPriceExclTVA || 0),
          UnitPriceInclTVA: Number(item.UnitPriceInclTVA || item.UnitPrice || item.Price || 0),
          TotalExcl: Number(item.TotalExcl || 0),
          TotalIncl: Number(item.TotalIncl || 0),
          VATAmount: Number(item.VATAmount || item.TotalVAT || 0),
          CostPrice: Number(item.CostPrice || 0),
          SalePrice: Number(item.Price || item.UnitPrice || 0),
          VATPercentage: Number(item.VAT || 0),
        })),
      };
      
      await saleOrderService.updateSaleOrder(currentSaleOrderId, updateData);
    } catch (error) {

    }
  };

  // Handle add product - saves directly to DB
  const handleAddProduct = async (productToAdd = null, quantityToAdd = null, priceToAdd = null, bypassStockCheck = false) => {
    setProductError("");
    
    // Check if sale order creation is in progress
    if (customerChangeInProgress) {
      setProductError(t('please_wait_creating_sale_order') || 'Please wait, creating sale order...');
      return;
    }
    
    // Use provided parameters or fall back to state
    const product = productToAdd || selectedProduct;
    const qty = quantityToAdd || quantity;
    const productPrice = priceToAdd || price;

    if (!product || product.value === '__no_customer__') {
      setProductError(t('please_select_product') || 'Please select a product');
      return;
    }
    
    // For temp products, skip validation (handled in DocumentProductsTable pending row)
    // Save to DB with null ProductId so the line item is persisted with just the name
    if (product.isTemp) {
      const unitPrice = parseFloat(productPrice) || 0;
      const validQty = qty > 0 ? qty : 1;
      
      let totalExcl, vatAmount, totalIncl;
      if (isTvaIncluded) {
        totalIncl = validQty * unitPrice;
        totalExcl = totalIncl / (1 + vatPercentage / 100);
        vatAmount = totalIncl - totalExcl;
      } else {
        totalExcl = validQty * unitPrice;
        vatAmount = vatPercentage > 0 ? totalExcl * (vatPercentage / 100) : 0;
        totalIncl = totalExcl + vatAmount;
      }

      const lineItemData = {
        ProductId: null,
        ProductName: product.Name || product.ProductName,
        Quantity: validQty,
        UnitPrice: unitPrice,
        UnitPriceExclTVA: isTvaIncluded ? (unitPrice / (1 + vatPercentage / 100)) : unitPrice,
        UnitPriceInclTVA: isTvaIncluded ? unitPrice : (unitPrice * (1 + vatPercentage / 100)),
        TotalExcl: totalExcl,
        VAT: vatPercentage,
        VATPercentage: vatPercentage,
        VATAmount: vatAmount,
        TotalIncl: totalIncl,
        CostPrice: null,
        IsDiscountApplied: true,
      };

      try {
        const urlParams = new URLSearchParams(window.location.search);
        const urlId = urlParams.get('id');
        const currentSaleOrderId = saleOrderId || saleOrderIdRef.current || urlId;

        if (currentSaleOrderId) {
          const addedItem = await saleOrderService.addSaleOrderLineItem(currentSaleOrderId, lineItemData);
          const savedItem = addedItem?.payload || addedItem?.data || addedItem;
          setSaleOrderProducts([...saleOrderProducts, {
            ...savedItem,
            _rowKey: Date.now(),
            Name: product.Name || product.ProductName,
            Price: unitPrice,
            CostPrice: null,
            BuyPrice: null,
            isTemp: true,
            applyDiscount: true,
          }]);
        } else {
          // No sale order yet — add to local state only
          setSaleOrderProducts([...saleOrderProducts, {
            ...lineItemData,
            Id: `temp_${Date.now()}`,
            _rowKey: Date.now(),
            Name: product.Name || product.ProductName,
            isTemp: true,
            applyDiscount: true,
          }]);
        }
      } catch (error) {

        // Still add locally so user doesn't lose work
        setSaleOrderProducts([...saleOrderProducts, {
          Id: `temp_${Date.now()}`,
          ProductId: null,
          ProductName: product.Name || product.ProductName,
          Name: product.Name || product.ProductName,
          Quantity: validQty,
          UnitPrice: unitPrice,
          Price: unitPrice,
          TotalExcl: totalExcl,
          VAT: vatPercentage,
          VATAmount: vatAmount,
          TotalIncl: totalIncl,
          isTemp: true,
          applyDiscount: true,
          _rowKey: Date.now(),
        }]);
      }

      setProductError("");
      return;
    }
    
    // For regular products (not temp), validate before adding
    if (qty <= 0) {
      setProductError(t('please_enter_valid_quantity') || 'Please enter valid quantity');
      return;
    }
    if (!productPrice || parseFloat(productPrice) <= 0) {
      setProductError(t('please_enter_valid_price') || 'Please enter valid price');
      return;
    }

    // Check stock quantity
    const stockQuantity = product.StockQuantity || 0;
    if (!bypassStockCheck && stockQuantity > 0 && qty > stockQuantity) {
      // Show stock warning dialog
      setStockWarningData({
        product,
        requestedQuantity: qty,
        stockQuantity,
        price: productPrice,
        action: 'add'
      });
      setStockWarningDialog(true);
      return;
    }

    // Check if product already exists in the list with the same price
    const existingProductIndex = saleOrderProducts.findIndex(
      p => (p.ProductId === product.Id || p.Id === product.Id) && 
           parseFloat(p.Price || p.UnitPrice) === parseFloat(productPrice)
    );

    if (existingProductIndex !== -1) {
      // Product exists with same price - update quantity instead of adding duplicate
      const existingProduct = saleOrderProducts[existingProductIndex];
      const newQuantity = parseFloat(existingProduct.Quantity) + parseFloat(qty);
      await handleQuantityChange(existingProduct, newQuantity);
      
      // Clear selection if called without parameters
      if (!productToAdd) {
        setSelectedProduct(null);
        setQuantity(1);
        setPrice("");
      }
      
      return;
    }

    const unitPrice = parseFloat(productPrice) || 0;
    
    // Calculate based on isTvaIncluded
    let totalExcl, vatAmount, totalIncl;
    if (isTvaIncluded) {
      // Price includes VAT - extract VAT from total
      totalIncl = qty * unitPrice;
      totalExcl = totalIncl / (1 + vatPercentage / 100);
      vatAmount = totalIncl - totalExcl;
    } else {
      // Price excludes VAT - add VAT on top
      totalExcl = qty * unitPrice;
      vatAmount = vatPercentage > 0 ? totalExcl * (vatPercentage / 100) : 0;
      totalIncl = totalExcl + vatAmount;
    }



    const lineItemData = {
      ProductId: product.Id,
      ProductName: product.Name,
      SKUBarCode: product.SKU || product.BarCode || product.SKUBarCode || null,
      Quantity: qty,
      UnitPrice: unitPrice,
      UnitPriceExclTVA: isTvaIncluded ? (unitPrice / (1 + vatPercentage / 100)) : unitPrice,
      UnitPriceInclTVA: isTvaIncluded ? unitPrice : (unitPrice * (1 + vatPercentage / 100)),
      TotalExcl: totalExcl,
      VAT: vatPercentage,
      VATPercentage: vatPercentage,
      VATAmount: vatAmount,
      TotalIncl: totalIncl,
      CostPrice: product.CostPrice || product.BuyPrice || null,
      IsDiscountApplied: true,
    };

    try {
      // Sale order should already exist (created on customer selection or loaded from existing)
      // Use ref as fallback for immediate access after creation
      // Also check URL for ID (in case of page refresh or async timing)
      const urlParams = new URLSearchParams(window.location.search);
      const urlId = urlParams.get('id');
      const currentSaleOrderId = saleOrderId || saleOrderIdRef.current || urlId;
      
      if (!currentSaleOrderId) {
        setProductError(t('sale_order_not_created') || 'Sale order not created');
        return;
      }

      // Add line item to existing sale order
      const addedItem = await saleOrderService.addSaleOrderLineItem(currentSaleOrderId, lineItemData);
      const savedItem = addedItem?.payload || addedItem?.data || addedItem;
      
      const updatedProducts = [
        ...saleOrderProducts,
        {
          ...savedItem,
          _rowKey: Date.now(),
          Name: product.Name,
          SKU: product.SKU || null,
          BarCode: product.BarCode || null,
          SKUBarCode: product.SKUBarCode || null,
          Price: unitPrice,
          CostPrice:  product.CostPrice || 0, // Include cost price for profit calculation
          BuyPrice:  product.CostPrice || 0,
          isTemp: product.isTemp || false,
          applyDiscount: true, // Default: discount applies to this item
          StockQuantity: product.StockQuantity || 0, // Include stock quantity for validation
          MainStoreStock: product.MainStoreStock || 0,
          VirtualStoreStock: product.VirtualStoreStock || 0,
        }
      ];
      
      setSaleOrderProducts(updatedProducts);
      
      // Update sale order totals
      await updateSaleOrderTotals(updatedProducts);

      // Only clear selection if called without parameters (from state-based form)
      if (!productToAdd) {
        setSelectedProduct(null);
        setQuantity(1);
        setPrice("");
      }
      setProductError("");
      // Product added successfully
    } catch (error) {

      setProductError(error.message || t('failed_to_add_product') || 'Failed to add product');
    }
  };

  // Handle remove product - deletes from DB
  const handleRemoveProduct = async (product) => {
    // If it's a temp product, just remove from local state (not in DB)
    if (product.isTemp) {
      setSaleOrderProducts(saleOrderProducts.filter((p) => p._rowKey !== product._rowKey));
      return;
    }
    
    const urlParams = new URLSearchParams(window.location.search);
    const urlId = urlParams.get('id');
    const currentSaleOrderId = saleOrderId || saleOrderIdRef.current || urlId;
    
    if (!currentSaleOrderId) {
      // Just remove from local state if not saved to DB yet
      setSaleOrderProducts(saleOrderProducts.filter((p) => p._rowKey !== product._rowKey));
      return;
    }

    try {
      await saleOrderService.deleteSaleOrderLineItem(currentSaleOrderId, product.Id || product.LineItemId);
      const updatedProducts = saleOrderProducts.filter((p) => p._rowKey !== product._rowKey);
      setSaleOrderProducts(updatedProducts);
      
      // Update sale order totals
      await updateSaleOrderTotals(updatedProducts);
      // Product removed - no toast needed
    } catch (error) {

      setProductError(error.message || t('failed_to_remove_product') || 'Failed to remove product');
    }
  };

  // Handle discount toggle for individual products
  const handleDiscountToggle = (product) => {
    setSaleOrderProducts(saleOrderProducts.map(p => 
      p._rowKey === product._rowKey 
        ? { ...p, applyDiscount: p.applyDiscount === false ? true : false }
        : p
    ));
  };

  // Calculate totals with discount based on price type and applyDiscount flag
  const totalExcl = saleOrderProducts.reduce((sum, p) => sum + Number(p.TotalExcl || 0), 0);
  const totalIncl = saleOrderProducts.reduce((sum, p) => sum + Number(p.TotalIncl || 0), 0);
  
  // Calculate totals for products eligible for discount
  const discountableExcl = saleOrderProducts
    .filter(p => p.applyDiscount !== false)
    .reduce((sum, p) => sum + Number(p.TotalExcl || 0), 0);
  const discountableIncl = saleOrderProducts
    .filter(p => p.applyDiscount !== false)
    .reduce((sum, p) => sum + Number(p.TotalIncl || 0), 0);
  
  // Calculate discount amount - only on products with applyDiscount = true
  let discountAmount = 0;
  let subtotalAfterDiscount = 0;
  let vatAfterDiscount = 0;
  let grandTotal = 0;
  
  if (isTvaIncluded) {
    // Prices already include VAT
    if (vatPercentage > 0) {
      // When VAT included and rate not zero, apply discount on excl VAT amount
      const totalExclVAT = totalIncl / (1 + vatPercentage / 100);
      const discountableExclVAT = discountableIncl / (1 + vatPercentage / 100);
      if (discountValue > 0) {
        if (discountType === "percentage") {
          discountAmount = Number(discountableExclVAT * (discountValue / 100)) || 0;
        } else {
          discountAmount = Number(discountValue) || 0;
        }
      }
      const subtotalExclAfterDiscount = totalExclVAT - discountAmount;
      vatAfterDiscount = subtotalExclAfterDiscount * (vatPercentage / 100);
      grandTotal = subtotalExclAfterDiscount + vatAfterDiscount;
      subtotalAfterDiscount = grandTotal;
    } else {
      // VAT is 0, apply discount on total
      if (discountValue > 0) {
        if (discountType === "percentage") {
          discountAmount = Number(discountableIncl * (discountValue / 100)) || 0;
        } else {
          discountAmount = Number(discountValue) || 0;
        }
      }
      subtotalAfterDiscount = totalIncl - discountAmount;
      grandTotal = subtotalAfterDiscount;
      vatAfterDiscount = 0;
    }
  } else {
    // Prices exclude VAT - calculate VAT on subtotal after discount
    if (discountValue > 0) {
      if (discountType === "percentage") {
        discountAmount = Number(discountableExcl * (discountValue / 100)) || 0;
      } else {
        discountAmount = Number(discountValue) || 0;
      }
    }
    subtotalAfterDiscount = totalExcl - discountAmount;
    vatAfterDiscount = vatPercentage > 0 ? subtotalAfterDiscount * (vatPercentage / 100) : 0;
    grandTotal = subtotalAfterDiscount + vatAfterDiscount;
  }

  const outstandingTotal = showOutstanding ? selectedInvoices.reduce((sum, inv) => sum + parseFloat(inv.DueAmount || 0), 0) : 0;
  
  // Calculate profit (if products have CostPrice) - with discount consideration
  const totalCost = saleOrderProducts.reduce((sum, p) => {
    const costPrice = Number(p.CostPrice || 0);
    const quantity = Number(p.Quantity || 0);
    return sum + (costPrice * quantity);
  }, 0);
  // Profit = (Total excluding VAT - Discount - Total Cost)
  const actualSalesAmount = isTvaIncluded ? (subtotalAfterDiscount / (1 + vatPercentage / 100)) : subtotalAfterDiscount;
  const totalProfit = actualSalesAmount - totalCost;
  const profitMargin = actualSalesAmount > 0 ? ((totalProfit / actualSalesAmount) * 100) : 0;

  // Handle save as draft
  const handleSaveAsDraft = () => {
    const errors = [];
    
    if (!selectedCustomer) {
      errors.push(t('please_select_customer') || 'Please select a customer');
    }
    
    if (saleOrderProducts.length === 0) {
      errors.push(t('please_add_at_least_one_product') || 'Please add at least one product');
    }
    
    if (errors.length > 0) {
      setGeneralErrors(errors);
      return;
    }

    setGeneralErrors([]);
    setDraftConfirmDialog(true);
  };

  const confirmSaveAsDraft = async () => {
    setLoading(true);
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const urlId = urlParams.get('id');
      const currentSaleOrderId = saleOrderId || saleOrderIdRef.current || urlId;
      
      if (!currentSaleOrderId) {
        setGeneralErrors([t('sale_order_not_created') || 'Sale order not created']);
        return;
      }
      
      // Calculate discount amount based on type
      let calculatedDiscountAmount = 0;
      if (discountValue > 0) {
        if (discountType === "percentage") {
          const discountableAmount = isTvaIncluded ? discountableIncl : discountableExcl;
          calculatedDiscountAmount = Number(discountableAmount * (discountValue / 100)) || 0;
        } else {
          calculatedDiscountAmount = Number(discountValue) || 0;
        }
      }
      
      // Update sale order with latest data and keep status as Draft
      const updateData = {
        SaleOrderNumber: saleOrderNumber,
        SaleOrderDate: saleOrderDate,
        CustomerId: selectedCustomer?.Id,
        CustomerName: selectedCustomer?.CompanyName || selectedCustomer?.Name,
        CustomText: customText,
        VATPercentage: vatPercentage,
        Status: 'Draft',
        DiscountType: discountType === 'percentage' ? 'Percentage' : 'Fixed',
        DiscountPercentage: discountType === 'percentage' ? discountValue : null,
        DiscountAmount: discountType === 'fixed' ? discountValue : calculatedDiscountAmount,
        TotalExcl: totalExcl,
        TotalVAT: vatAfterDiscount,
        TotalIncl: grandTotal,
        IsTvaIncluded: isTvaIncluded,
        TotalProfit: totalProfit,
        LineItems: saleOrderProducts.map((item) => ({
          Id: item.Id ?? item.LineItemId ?? undefined,
          ProductId: item.ProductId ?? null,
          ProductName: item.Name || item.ProductName || item.DisplayName || '',
          SKUBarCode: item.SKU || item.BarCode || item.SKUBarCode || null,
          Quantity: Number(item.Quantity || 1),
          UnitPrice: Number(item.UnitPrice || item.Price || 0),
          UnitPriceExclTVA: Number(item.UnitPriceExclTVA || 0),
          UnitPriceInclTVA: Number(item.UnitPriceInclTVA || item.UnitPrice || item.Price || 0),
          TotalExcl: Number(item.TotalExcl || 0),
          TotalIncl: Number(item.TotalIncl || 0),
          VATAmount: Number(item.VATAmount || item.TotalVAT || 0),
          CostPrice: Number(item.CostPrice || 0),
        })),
      };
      await saleOrderService.updateSaleOrder(currentSaleOrderId, updateData);
      
      // Update last saved state after successful save
      lastSavedStateRef.current = {
        customText: customText || '',
        discountValue: parseFloat(discountValue) || 0,
        discountType: discountType,
        vatPercentage: Number(vatPercentage) || 0,
        isTvaIncluded: Boolean(isTvaIncluded),
      };
      
      // Sale order saved successfully - navigate
      navigate('/sales/sale-orders');
    } catch (error) {

      setGeneralErrors([error.message || t('failed_to_save_draft') || 'Failed to save draft']);
    } finally {
      setLoading(false);
      setDraftConfirmDialog(false);
    }
  };

  // Handle close sale order
  const handleCloseSaleOrder = () => {
    const errors = [];
    
    if (!selectedCustomer) {
      errors.push(t('please_select_customer') || 'Please select a customer');
    }
    
    if (saleOrderProducts.length === 0) {
      errors.push(t('please_add_at_least_one_product') || 'Please add at least one product');
    }
    
    if (errors.length > 0) {
      setGeneralErrors(errors);
      return;
    }

    setGeneralErrors([]);
    setSaveConfirmDialog(true);
  };

  const confirmCloseSaleOrder = async () => {
    setLoading(true);
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const urlId = urlParams.get('id');
      const currentSaleOrderId = saleOrderId || saleOrderIdRef.current || urlId;
      
      if (!currentSaleOrderId) {
        setGeneralErrors([t('sale_order_not_created') || 'Sale order not created']);
        return;
      }
      
      // Calculate discount amount based on type
      let calculatedDiscountAmount = 0;
      if (discountValue > 0) {
        if (discountType === "percentage") {
          const discountableAmount = isTvaIncluded ? discountableIncl : discountableExcl;
          calculatedDiscountAmount = Number(discountableAmount * (discountValue / 100)) || 0;
        } else {
          calculatedDiscountAmount = Number(discountValue) || 0;
        }
      }
      
      const paid = parseFloat(invoicePaidAmount) || 0;
      const due = grandTotal - paid;

      const updateData = {
        Id: parseInt(currentSaleOrderId),
        SaleOrderNumber: saleOrderNumber,
        SaleOrderDate: saleOrderDate,
        CustomerId: selectedCustomer?.Id,
        CustomerName: selectedCustomer?.CompanyName || selectedCustomer?.Name,
        CustomText: customText ?? null,
        VATPercentage: vatPercentage ?? 0,
        Status: 'Closed',
        DiscountType: discountType === 'percentage' ? 'Percentage' : 'Fixed',
        DiscountPercentage: discountType === 'percentage' ? discountValue : null,
        DiscountAmount: discountType === 'fixed' ? discountValue : calculatedDiscountAmount,
        TotalExcl: totalExcl,
        TotalVAT: vatAfterDiscount,
        TotalIncl: grandTotal,
        PaidAmount: paid,
        DueAmount: due,
        PaymentStatus: paid >= grandTotal ? 'Paid' : paid > 0 ? 'Partial Paid' : 'Not Paid',
        IsTvaIncluded: isTvaIncluded,
        TotalProfit: totalProfit ?? null,
        LineItems: saleOrderProducts.map((item) => ({
          Id: item.Id ?? item.LineItemId ?? undefined,
          ProductId: item.ProductId ?? null,
          ProductName: item.Name || item.ProductName || item.DisplayName || '',
          SKUBarCode: item.SKU || item.BarCode || item.SKUBarCode || null,
          Quantity: Number(item.Quantity || 1),
          UnitPrice: Number(item.UnitPrice || item.Price || 0),
          UnitPriceExclTVA: Number(item.UnitPriceExclTVA || 0),
          UnitPriceInclTVA: Number(item.UnitPriceInclTVA || item.UnitPrice || item.Price || 0),
          TotalExcl: Number(item.TotalExcl || 0),
          TotalIncl: Number(item.TotalIncl || 0),
          VATAmount: Number(item.VATAmount || item.TotalVAT || 0),
          CostPrice: Number(item.CostPrice || 0),
        })),
      };
      await saleOrderService.updateSaleOrder(currentSaleOrderId, updateData);
      
      // Update last saved state after successful save
      lastSavedStateRef.current = {
        customText: customText || '',
        discountValue: parseFloat(discountValue) || 0,
        discountType: discountType,
        vatPercentage: Number(vatPercentage) || 0,
        isTvaIncluded: Boolean(isTvaIncluded),
      };
      
      // Sale order closed successfully - navigate to detail page
      navigate(`/sales/sale-orders/${currentSaleOrderId}`);
    } catch (error) {

      setGeneralErrors([error.message || t('failed_to_close_sale_order') || 'Failed to close sale order']);
    } finally {
      setLoading(false);
      setSaveConfirmDialog(false);
    }
  };

  // Handle preview PDF
  const handlePreviewPdf = async () => {
    const errors = [];
    
    if (!selectedCustomer) {
      errors.push(t('please_select_customer') || 'Please select a customer');
    }
    
    if (saleOrderProducts.length === 0) {
      errors.push(t('please_add_at_least_one_product') || 'Please add at least one product');
    }
    
    if (errors.length > 0) {
      setGeneralErrors(errors);
      return;
    }
    
    setGeneralErrors([]);

    // Calculate discountable amounts (only products with applyDiscount !== false)
    const discountableExcl = saleOrderProducts
      .filter(p => p.applyDiscount !== false)
      .reduce((sum, p) => sum + Number(p.TotalExcl || 0), 0);
    const discountableIncl = saleOrderProducts
      .filter(p => p.applyDiscount !== false)
      .reduce((sum, p) => sum + Number(p.TotalIncl || 0), 0);
    
    // Calculate actual discount amount based on discountable products
    let calculatedDiscountAmount = 0;
    if (discountValue > 0) {
      if (discountType === "percentage") {
        const discountableAmount = isTvaIncluded ? discountableIncl : discountableExcl;
        calculatedDiscountAmount = Number(discountableAmount * (discountValue / 100)) || 0;
      } else {
        // For fixed amount, only apply if there are discountable products
        calculatedDiscountAmount = (discountableExcl > 0 || discountableIncl > 0) ? Number(discountValue) || 0 : 0;
      }
    }

    const saleOrderData = {
      SaleOrderNumber: saleOrderNumber,
      SaleOrderDate: saleOrderDate,
      Customer: selectedCustomer,
      LineItems: saleOrderProducts,
      CustomText: customText,
      VATPercentage: vatPercentage,
      Status: status,
      PaymentStatus: 'Not Paid',
      PaidAmount: 0,
      TotalIncl: grandTotal,
      DiscountAmount: calculatedDiscountAmount,
      IsTvaIncluded: isTvaIncluded,
      PaymentMethod: '',
      PaymentMethods: [],
      OutstandingInvoices: showOutstanding ? selectedInvoices : [],
    };

    const htmlContent = generateSaleOrderPdfHtml(saleOrderData, storeInfo, language);
    setPreviewHtml(htmlContent);
    setOpenPreview(true);
  };

  // Handle new customer added
  const handleNewCustomer = (customer) => {
    setSelectedCustomer(customer);
    setOpenCustomerDialog(false);
    // Customer created and selected
  };

  // Handle new product added — put in pending row so user can confirm qty/price
  const handleNewProduct = (product) => {
    setOpenProductDialog(false);
    setProductSearchText('');
    // Place newly created product in the pending row (not directly added to list)
    setPendingProductForTable({ ...product, _ts: Date.now() });
  };

  // Fetch outstanding documents when customer changes
  useEffect(() => {
    if (selectedCustomer?.Id) {
      getCustomerOutstandingDocuments(selectedCustomer.Id)
        .then((data) => {
          let docs = [];
          if (data?.payload && Array.isArray(data.payload)) {
            docs = data.payload;
          } else if (Array.isArray(data)) {
            docs = data;
          }
          const filteredDocs = docs.filter(doc => !saleOrderId || !(String(doc.DocumentId) === String(saleOrderId)));
          setOutstandingInvoices(filteredDocs);
          // Select all outstanding invoices by default
          setSelectedInvoices(filteredDocs);
        })
        .catch(() => {
          setOutstandingInvoices([]);
          setSelectedInvoices([]);
        });
    } else {
      setOutstandingInvoices([]);
      setSelectedInvoices([]);
    }
  }, [selectedCustomer]);

  const handleInvoiceToggle = (invoice) => {
    setSelectedInvoices((prev) =>
      prev.some((i) => i.DocumentId === invoice.DocumentId && i.DocumentType === invoice.DocumentType)
        ? prev.filter((i) => !(i.DocumentId === invoice.DocumentId && i.DocumentType === invoice.DocumentType))
        : [...prev, invoice]
    );
  };

  // Handle select all outstanding invoices
  const handleSelectAllInvoices = (checked) => {
    if (checked) {
      setSelectedInvoices([...outstandingInvoices]);
    } else {
      setSelectedInvoices([]);
    }
  };

  // Load customer options with debounce
  const customerDebounceRef = React.useRef();
  const loadCustomerOptions = (inputValue, callback) => {
    if (customerDebounceRef.current) clearTimeout(customerDebounceRef.current);

    customerDebounceRef.current = setTimeout(async () => {
      if (!inputValue || inputValue.trim().length < 2) {
        return callback([]);
      }

      try {
        const customers = await searchCustomers(inputValue);
        const customersArray = Array.isArray(customers) ? customers : [];
        callback(customersArray);
      } catch (error) {

        callback([]);
      }
    }, 400);
  };

  // Load product options with debounce
  const debounceRef = React.useRef();
  const loadProductOptions = (inputValue, callback) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      if (!inputValue) return callback([]);
      
      // Read the latest customer from ref to avoid stale closures
      const currentCustomer = selectedCustomerRef.current;
      if (!currentCustomer) {
        return callback([{
          value: '__no_customer__',
          label: `⚠️ ${t('please_select_customer_first') || 'Please select a customer first'}`,
          isDisabled: true,
          styles: {
            option: (provided) => ({
              ...provided,
              color: '#d32f2f',
              fontWeight: 600,
              backgroundColor: '#ffebee',
            })
          }
        }]);
      }

      try {
        const barcodeLengths = [8, 12, 13, 14];
        let paramsObj;

        if (barcodeLengths.includes(inputValue.length) && /^\d+$/.test(inputValue)) {
          paramsObj = { skuBarCode: inputValue };
        } else if (/^\d+$/.test(inputValue)) {
          paramsObj = { skuBarCode: inputValue };
        } else if (inputValue.length >= 3) {
          paramsObj = { name: inputValue };
        } else {
          return callback([]);
        }

        const data = await searchProducts(paramsObj);

        const productsArray = Array.isArray(data?.payload)
          ? data.payload
          : Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : [];

        const limitedProducts = productsArray.slice(0, 50);
        
        const productOptions = limitedProducts.map((p) => ({
          value: p.Id,
          label: `${p.Name} (€${p.SalePrice || 0})${p.SKU ? " - SKU: " + p.SKU : ""}${p.BarCode ? " - BarCode: " + p.BarCode : ""}`,
          MainStoreStock: p.MainStoreStock || 0,
          VirtualStoreStock: p.VirtualStoreStock || 0,
          ...p,
        }));
        
        // Always add custom options if input is long enough (even when no products found)
        if (inputValue.length >= 3) {
          productOptions.push({
            value: '__add_new__',
            label: `➕ ${t('add_new_product')} "${inputValue}"`,
            isCustomOption: true,
            searchText: inputValue,
          });
          productOptions.push({
            value: '__temp_add__',
            label: `📝 ${t('add_temp_product')} "${inputValue}" (${t('only_for_this_order')})`,
            isCustomOption: true,
            isTempProduct: true,
            searchText: inputValue,
          });
        }

        callback(productOptions);
      } catch (error) {

        // Even on error, show custom options if input is long enough
        const customOptions = [];
        if (inputValue.length >= 3) {
          customOptions.push({
            value: '__add_new__',
            label: `➕ ${t('add_new_product')} "${inputValue}"`,
            isCustomOption: true,
            searchText: inputValue,
          });
          customOptions.push({
            value: '__temp_add__',
            label: `📝 ${t('add_temp_product')} "${inputValue}" (${t('only_for_this_order')})`,
            isCustomOption: true,
            isTempProduct: true,
            searchText: inputValue,
          });
        }
        callback(customOptions);
      }
    }, 350);
  };

  // Handle quantity change in table with debounced auto-save
  // Stock warning dialog confirm — continue with the originally-requested quantity
  const handleContinueWithStock = () => {
    if (!stockWarningData) return;
    if (stockWarningData.action === 'add') {
      handleAddProduct(stockWarningData.product, stockWarningData.requestedQuantity, stockWarningData.price, true);
    } else {
      handleQuantityChange(stockWarningData.product, stockWarningData.requestedQuantity, true);
    }
    setStockWarningDialog(false);
    setStockWarningData(null);
  };

  const handleQuantityChange = async (product, newQuantity, bypassStockCheck = false) => {
    // Allow 0 during editing (user might be clearing field), but skip processing if 0
    if (newQuantity === 0 || newQuantity === '') {
      // Just update the display value without calculations
      const updatedProducts = saleOrderProducts.map((p) => {
        if (p._rowKey === product._rowKey) {
          return {
            ...p,
            Quantity: newQuantity === '' ? '' : newQuantity
          };
        }
        return p;
      });
      setSaleOrderProducts(updatedProducts);
      return;
    }
    
    if (newQuantity < 0) return;
    
    // Check stock quantity (unless bypassed)
    if (!bypassStockCheck && !product.isTemp) {
      const stockQuantity = product.StockQuantity || 0;
      if (stockQuantity > 0 && newQuantity > stockQuantity) {
        // Show stock warning dialog
        setStockWarningData({
          product,
          requestedQuantity: newQuantity,
          stockQuantity,
          price: product.Price || product.UnitPrice,
          action: 'update'
        });
        setStockWarningDialog(true);
        return;
      }
    }
    
    const updatedProducts = saleOrderProducts.map((p) => {
      if (p._rowKey === product._rowKey) {
        const pricePerUnit = parseFloat(p.UnitPrice || p.Price);
        
        let newTotalExcl, newVATAmount, newTotalIncl;
        if (isTvaIncluded) {
          // Price includes VAT
          newTotalIncl = pricePerUnit * newQuantity;
          newTotalExcl = newTotalIncl / (1 + vatPercentage / 100);
          newVATAmount = newTotalIncl - newTotalExcl;
        } else {
          // Price excludes VAT
          newTotalExcl = pricePerUnit * newQuantity;
          newVATAmount = vatPercentage > 0 ? newTotalExcl * (vatPercentage / 100) : 0;
          newTotalIncl = newTotalExcl + newVATAmount;
        }
        
        // Clear quantity validation error when user fixes it
        const updatedValidationErrors = p._validationErrors ? {
          ...p._validationErrors,
          quantity: newQuantity <= 0
        } : undefined;
        
        return {
          ...p,
          Quantity: newQuantity,
          TotalExcl: newTotalExcl.toFixed(2),
          VATAmount: newVATAmount.toFixed(2),
          TotalIncl: newTotalIncl.toFixed(2),
          _validationErrors: updatedValidationErrors
        };
      }
      return p;
    });
    setSaleOrderProducts(updatedProducts);
    
    // Update in database (skip for temp products) - with debounce
    if (product.isTemp) {
      return; // Temp products are not saved to DB
    }
    
    // Clear existing timer for this product
    if (quantityUpdateTimerRef.current[product._rowKey]) {
      clearTimeout(quantityUpdateTimerRef.current[product._rowKey]);
    }
    
    // Set new timer for debounced API call (2.5 seconds)
    quantityUpdateTimerRef.current[product._rowKey] = setTimeout(async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const urlId = urlParams.get('id');
        const currentSaleOrderId = saleOrderId || saleOrderIdRef.current || urlId;
        
        if (currentSaleOrderId && product.Id) {
          const updatedProduct = updatedProducts.find(p => p._rowKey === product._rowKey);
          if (updatedProduct) {
            const unitPrice = parseFloat(updatedProduct.UnitPrice || updatedProduct.Price);
            await saleOrderService.updateSaleOrderLineItem(currentSaleOrderId, product.Id, {
              ProductId: updatedProduct.ProductId,
              ProductName: updatedProduct.ProductName || updatedProduct.Name,
              SKUBarCode: updatedProduct.SKU || updatedProduct.BarCode || updatedProduct.SKUBarCode || null,
              Quantity: newQuantity,
              UnitPrice: unitPrice,
              UnitPriceExclTVA: isTvaIncluded ? (unitPrice / (1 + vatPercentage / 100)) : unitPrice,
              UnitPriceInclTVA: isTvaIncluded ? unitPrice : (unitPrice * (1 + vatPercentage / 100)),
              TotalExcl: parseFloat(updatedProduct.TotalExcl),
              VAT: vatPercentage,
              VATPercentage: vatPercentage,
              VATAmount: parseFloat(updatedProduct.VATAmount),
              TotalIncl: parseFloat(updatedProduct.TotalIncl),
              CostPrice: updatedProduct.CostPrice || updatedProduct.BuyPrice || null,
              IsDiscountApplied: updatedProduct.applyDiscount !== false,
            });
            
            // Update sale order totals after line item update
            await updateSaleOrderTotals(updatedProducts);
          }
        }
      } catch (error) {

        // Continue without showing error to user
      } finally {
        // Clean up timer reference
        delete quantityUpdateTimerRef.current[product._rowKey];
      }
    }, 2500); // 2.5 second debounce
  };

  // Handle price change in table (no cursor jumping)
  const handlePriceChange = (product, newPrice) => {
    // Accept both string (during typing) and number (on blur)
    const priceValue = typeof newPrice === 'string' ? newPrice : newPrice;
    const numPrice = typeof newPrice === 'string' ? parseFloat(newPrice) || 0 : newPrice;
    
    if (numPrice < 0) return;
    
    const updatedProducts = saleOrderProducts.map((p) => {
      if (p._rowKey === product._rowKey) {
        let newTotalExcl, newVATAmount, newTotalIncl;
        if (isTvaIncluded) {
          // Price includes VAT
          newTotalIncl = numPrice * p.Quantity;
          newTotalExcl = newTotalIncl / (1 + vatPercentage / 100);
          newVATAmount = newTotalIncl - newTotalExcl;
        } else {
          // Price excludes VAT
          newTotalExcl = numPrice * p.Quantity;
          newVATAmount = vatPercentage > 0 ? newTotalExcl * (vatPercentage / 100) : 0;
          newTotalIncl = newTotalExcl + newVATAmount;
        }
        
        // Clear price validation error when user fixes it
        const updatedValidationErrors = p._validationErrors ? {
          ...p._validationErrors,
          price: numPrice <= 0
        } : undefined;
        
        return {
          ...p,
          Price: typeof newPrice === 'string' ? priceValue : numPrice.toFixed(2),
          UnitPrice: typeof newPrice === 'string' ? priceValue : numPrice.toFixed(2),
          TotalExcl: newTotalExcl.toFixed(2),
          VATAmount: newVATAmount.toFixed(2),
          TotalIncl: newTotalIncl.toFixed(2),
          _validationErrors: updatedValidationErrors
        };
      }
      return p;
    });
    setSaleOrderProducts(updatedProducts);
  };

  // Handle price blur - save to database
  const handlePriceBlur = async (product, finalPrice) => {
    // Skip if temp product or invalid price
    if (product.isTemp || finalPrice <= 0) {
      return;
    }
    
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const urlId = urlParams.get('id');
      const currentSaleOrderId = saleOrderId || saleOrderIdRef.current || urlId;
      
      if (currentSaleOrderId && product.Id) {
        const updatedProduct = saleOrderProducts.find(p => p._rowKey === product._rowKey);
        if (updatedProduct) {
          const unitPrice = parseFloat(finalPrice);
          await saleOrderService.updateSaleOrderLineItem(currentSaleOrderId, product.Id, {
            ProductId: updatedProduct.ProductId,
            ProductName: updatedProduct.ProductName || updatedProduct.Name,
            SKUBarCode: updatedProduct.SKU || updatedProduct.BarCode || updatedProduct.SKUBarCode || null,
            Quantity: updatedProduct.Quantity,
            UnitPrice: unitPrice,
            UnitPriceExclTVA: isTvaIncluded ? (unitPrice / (1 + vatPercentage / 100)) : unitPrice,
            UnitPriceInclTVA: isTvaIncluded ? unitPrice : (unitPrice * (1 + vatPercentage / 100)),
            TotalExcl: parseFloat(updatedProduct.TotalExcl),
            VAT: vatPercentage,
            VATPercentage: vatPercentage,
            VATAmount: parseFloat(updatedProduct.VATAmount),
            TotalIncl: parseFloat(updatedProduct.TotalIncl),
            CostPrice: updatedProduct.CostPrice || updatedProduct.BuyPrice || null,
            IsDiscountApplied: updatedProduct.applyDiscount !== false,
          });
          
          // Update sale order totals after line item update
          await updateSaleOrderTotals(saleOrderProducts);
        }
      }
    } catch (error) {

    }
  };

  // Handle total incl change in table
  const handleTotalInclChange = (product, newTotalIncl) => {
    const updatedProducts = saleOrderProducts.map((p) => {
      if (p._rowKey === product._rowKey) {
        let newTotalExcl, newVATAmount, newPrice;
        
        if (vatPercentage > 0) {
          const vatRate = vatPercentage / 100;
          newTotalExcl = newTotalIncl / (1 + vatRate);
          newVATAmount = newTotalIncl - newTotalExcl;
        } else {
          // 0% VAT
          newTotalExcl = newTotalIncl;
          newVATAmount = 0;
        }
        
        newPrice = isTvaIncluded 
          ? (newTotalIncl / p.Quantity).toFixed(2) 
          : (newTotalExcl / p.Quantity).toFixed(2);
        
        return {
          ...p,
          TotalIncl: newTotalIncl.toFixed(2),
          TotalExcl: newTotalExcl.toFixed(2),
          VATAmount: newVATAmount.toFixed(2),
          Price: newPrice,
          UnitPrice: newPrice,
        };
      }
      return p;
    });
    setSaleOrderProducts(updatedProducts);
  };



  return (
    <Box sx={{
      background: 'linear-gradient(160deg, #fff3e0 0%, #fce4ec 50%, #fff8f0 100%)',
      minHeight: '100vh',
      py: 3
    }}>
      <Container 
        maxWidth={false}
        sx={{ 
          px: { xs: 2, sm: 3, md: 4, lg: 6 },
          maxWidth: '1600px',
          margin: '0 auto'
        }}
      >
        {/* Sale Order Container - Matches DeliveryNote A4 Style */}
        <Paper
          elevation={3}
          sx={{
            background: '#fffaf7',
            boxShadow: '0 0 24px rgba(230,81,0,0.10)',
            borderRadius: '8px',
            overflow: 'visible',
            maxWidth: '100%',
            position: 'relative',
            borderTop: '4px solid #e65100',
          }}
        >
          {/* Status Ribbon - Top Left Corner */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              bgcolor: status === 'Closed' ? '#4caf50' : '#ff9800',
              color: 'white',
              px: 3,
              py: 1,
              fontWeight: 700,
              fontSize: '0.875rem',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              borderTopLeftRadius: '8px',
              borderBottomRightRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              zIndex: 10
            }}
          >
            {status === 'Closed' ? t('closed') : t('draft')}
          </Box>
          
          <Box sx={{ p: { xs: 3, sm: 4, md: 5, lg: 6 }, pt: { xs: 6, sm: 6, md: 7, lg: 8 } }}>
            {/* TOP SECTION: Company Info (Left) + Customer (Right) - Side by Side */}
            <DocumentHeaderSection
              storeInfo={storeInfo}
              selectedCustomer={selectedCustomer}
              onCustomerChange={setSelectedCustomer}
              onCustomerClear={() => setSelectedCustomer(null)}
              onAddCustomerClick={() => setOpenCustomerDialog(true)}
              onEditCustomerClick={() => setOpenEditCustomerDialog(true)}
              loadCustomerOptions={loadCustomerOptions}
              disabled={status === 'Closed'}
              documentLabel={t('sale_order') || 'Sale Order'}
              accentColor="#e65100"
            />

            {/* Customer Error Display */}
            {customerError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {customerError}
              </Alert>
            )}

            {/* SALE ORDER DETAILS ROW: Sale Order Number, Date, VAT Rate, Items Count */}
            <DocumentDetailsBar
              documentDate={saleOrderDate}
              onDateChange={(date) => setSaleOrderDate(date.toISOString().split('T')[0])}
              vatPercentage={vatPercentage}
              onVatChange={setVatPercentage}
              vatRates={vatRates}
              itemsCount={saleOrderProducts.length}
              isTvaIncluded={isTvaIncluded}
              onTvaToggle={(checked) => setIsTvaIncluded(checked)}
              dateLabel={t("sale_order_date")}
              disableDateEdit={true}
              showPriceTypeToggle={true}
              showProfit={showProfit}
              onProfitToggle={setShowProfit}
              saleOrderNumber={saleOrderNumber}
              status={status}
              saleOrderId={saleOrderId || saleOrderIdRef.current}
              onSaveDraft={handleSaveAsDraft}
              onCloseOrder={handleCloseSaleOrder}
              disabled={status === 'Closed'}
            />

            {/* PRODUCTS SECTION: Table with Header Always Visible */}
            <DocumentProductsTable
              products={saleOrderProducts}
              onProductAdd={handleAddProduct}
              onProductRemove={handleRemoveProduct}
              onQuantityChange={handleQuantityChange}
              onPriceChange={handlePriceChange}
              onPriceBlur={handlePriceBlur}
              onTotalInclChange={handleTotalInclChange}
              onAddProductClick={() => setOpenProductDialog(true)}
              loadProductOptions={loadProductOptions}
              productSearchKey={selectedCustomer?.Id || 'none'}
              vatPercentage={vatPercentage}
              isTvaIncluded={isTvaIncluded}
              showProfit={showProfit}
              showDiscountColumn={discountValue > 0}
              onDiscountToggle={handleDiscountToggle}
              externalPendingProduct={pendingProductForTable}
              onExternalPendingConsumed={() => setPendingProductForTable(null)}
              onCustomProductOptionSelect={(type, searchText) => {
                if (type === 'add_new') {
                  setProductSearchText(searchText);
                  setOpenProductDialog(true);
                }
              }}
              convertToInvoiceMode={status === 'Closed'}
            />

            {/* Product Error Display */}
            {productError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {productError}
              </Alert>
            )}

            {/* BOTTOM SECTION: Discount, Notes and Totals */}
            <Grid container spacing={3}>
              {/* LEFT: Discount, Notes */}
              <Grid item xs={12} md={7}>
                <Stack spacing={3}>
                  {/* Discount Section */}
                  <Box sx={{
                    p: 3,
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    borderLeft: '4px solid #ff9800',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                  }}>
                    <Typography variant="subtitle2" fontWeight="700" color="#2c3e50" sx={{ textTransform: 'uppercase', mb: 1.5, fontSize: '0.85rem' }}>
                      <DiscountIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                      {t("discount")}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <FormControl fullWidth size="small">
                          <Select
                            value={discountType}
                            onChange={(e) => setDiscountType(e.target.value)}
                            disabled={saleOrderProducts.length === 0 || status === 'Closed'}
                            sx={{ bgcolor: 'white' }}
                          >
                            <MenuItem value="percentage">{t("percentage")}</MenuItem>
                            <MenuItem value="fixed">{t("fixed_amount")}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          value={discountValue}
                          onFocus={(e) => {
                            // Store original value when field is focused
                            originalValuesRef.current.discountValue = parseFloat(e.target.value) || 0;
                          }}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Allow empty string, numbers with optional decimal point (max 2 decimals)
                            if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                              setDiscountValue(value);
                            }
                          }}
                          onBlur={(e) => {
                            const originalValue = originalValuesRef.current.discountValue || 0;
                            const numValue = parseFloat(e.target.value);
                            
                            // Ensure we have a valid number
                            let finalValue = 0;
                            if (!isNaN(numValue) && numValue >= 0) {
                              finalValue = parseFloat(numValue.toFixed(2));
                            }
                            
                            // Only update state if value actually changed from original
                            if (finalValue !== originalValue) {
                              setDiscountValue(finalValue);
                            } else if (e.target.value === '' || e.target.value !== finalValue.toString()) {
                              // If current display value is invalid/empty but original was valid, reformat to show proper value
                              setDiscountValue(finalValue);
                            }
                            
                            // Clean up
                            delete originalValuesRef.current.discountValue;
                          }}
                          size="small"
                          fullWidth
                          disabled={saleOrderProducts.length === 0 || status === 'Closed'}
                          inputProps={{ min: 0, step: 0.01 }}
                          sx={{ bgcolor: 'white' }}
                          InputProps={{
                            endAdornment: (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2">{discountType === "percentage" ? "%" : "€"}</Typography>
                                {discountValue > 0 && status !== 'Closed' && (
                                  <IconButton
                                    size="small"
                                    onClick={() => setDiscountValue(0)}
                                    sx={{ p: 0.5 }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                            )
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Notes Section */}
                  <Box sx={{
                    p: 3,
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    borderLeft: '4px solid #667eea',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                  }}>
                    <Typography variant="subtitle2" fontWeight="700" color="#2c3e50" sx={{ textTransform: 'uppercase', mb: 1.5, fontSize: '0.85rem' }}>
                      <NotesIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                      {t("notes")}
                    </Typography>
                    <TextField
                      multiline
                      rows={4}
                      value={customText}
                      onChange={(e) => {
                        const newText = e.target.value;
                        if (newText.length <= MAX_CUSTOM_TEXT_LENGTH) {
                          setCustomText(newText);
                        }
                      }}
                      placeholder={t("add_custom_notes") || "Add custom notes..."}
                      fullWidth
                      disabled={status === 'Closed'}
                      sx={{
                        bgcolor: 'white',
                        borderRadius: '4px'
                      }}
                    />
                    <Typography
                      variant="caption"
                      color={customText.length > MAX_CUSTOM_TEXT_LENGTH ? 'error' : 'text.secondary'}
                      sx={{ mt: 1, display: 'block' }}
                    >
                      {customText.length} / {MAX_CUSTOM_TEXT_LENGTH}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              {/* RIGHT: Totals Summary & Action Buttons */}
              <Grid item xs={12} md={5}>
                <Stack spacing={3}>
                  {/* Outstanding Invoices Section — above summary */}
                  {outstandingInvoices.length > 0 && (
                    <Box sx={{ border: "1px solid #ffe082", borderRadius: "8px", overflow: "hidden" }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5, bgcolor: "#fff8e1", borderBottom: showOutstanding ? "1px solid #ffe082" : "none" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <ReceiptLongIcon fontSize="small" sx={{ color: "#f57c00" }} />
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{t("include_outstanding") || "Include Outstanding"}</Typography>
                            <Typography variant="caption" color="text.secondary">{outstandingInvoices.length} {t("documents") || "document(s)"}{selectedInvoices.length > 0 && ` · ${selectedInvoices.length} ${t("selected") || "selected"}`}</Typography>
                          </Box>
                        </Box>
                        <Switch checked={showOutstanding} onChange={(e) => setShowOutstanding(e.target.checked)} color="warning" size="small" />
                      </Box>
                      {showOutstanding && (
                        <Box sx={{ p: 1 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ bgcolor: alpha("#f57c00", 0.06) }}>
                                <TableCell sx={{ fontWeight: "bold", fontSize: "0.78rem", py: 0.8 }}>{t("document_number") || "Doc #"}</TableCell>
                                <TableCell sx={{ fontWeight: "bold", fontSize: "0.78rem", py: 0.8 }}>{t("type") || "Type"}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: "bold", fontSize: "0.78rem", py: 0.8 }}>{t("due_amount") || "Due"}</TableCell>
                                <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "0.78rem", py: 0.8 }}>
                                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                                    <Checkbox 
                                      checked={selectedInvoices.length === outstandingInvoices.length && outstandingInvoices.length > 0}
                                      indeterminate={selectedInvoices.length > 0 && selectedInvoices.length < outstandingInvoices.length}
                                      onChange={(e) => handleSelectAllInvoices(e.target.checked)}
                                      color="warning"
                                      size="small"
                                    />
                                    {t("include") || "✓"}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {outstandingInvoices.map((doc) => (
                                <TableRow key={`${doc.DocumentType}-${doc.DocumentId}`} hover>
                                  <TableCell sx={{ fontSize: "0.78rem", py: 0.8 }}>{doc.DocumentNumber || "—"}</TableCell>
                                  <TableCell sx={{ py: 0.8 }}><Chip label={doc.DocumentType || "Invoice"} size="small" color={doc.DocumentType === "Invoice" ? "primary" : "secondary"} variant="outlined" sx={{ fontSize: "0.68rem" }} /></TableCell>
                                  <TableCell align="right" sx={{ py: 0.8 }}><Chip label={`€${parseFloat(doc.DueAmount || 0).toFixed(2)}`} color="warning" size="small" variant="outlined" sx={{ fontSize: "0.7rem" }} /></TableCell>
                                  <TableCell align="center" sx={{ py: 0.8 }}><Checkbox checked={selectedInvoices.some((i) => i.DocumentId === doc.DocumentId && i.DocumentType === doc.DocumentType)} onChange={() => handleInvoiceToggle(doc)} color="warning" size="small" /></TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      )}
                    </Box>
                  )}
                  {/* Totals Summary Box */}
                  <Box sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    width: '100%'
                  }}>
                        <Box sx={{ p: 2, background: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle1" fontWeight="700" color="#2c3e50" sx={{ textTransform: 'uppercase', fontSize: '0.9rem' }}>
                              <CalculateIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                              {t("summary")}
                            </Typography>
                            <Tooltip title={showProfit ? t('hide_cost_prices') || 'Hide Cost Prices' : t('show_cost_prices') || 'Show Cost Prices'}>
                              <IconButton
                                size="small"
                                onClick={() => setShowProfit(!showProfit)}
                                sx={{
                                  color: showProfit ? '#4caf50' : '#666',
                                  '&:hover': { bgcolor: alpha(showProfit ? '#4caf50' : '#666', 0.1) }
                                }}
                              >
                                {showProfit ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                        <Box sx={{ p: 2 }}>
                          <Stack spacing={1.5}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" color="#555">{t("subtotal")}</Typography>
                              <Typography variant="body1" fontWeight={600}>€{(isTvaIncluded ? totalIncl : totalExcl).toFixed(2)}</Typography>
                            </Box>
                            
                            {/* Discount Display */}
                            {discountValue > 0 && (
                              <>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#fff3e0', borderRadius: '4px' }}>
                                  <Typography variant="body2" color="#ff9800" fontWeight={600}>
                                    {t("discount")} {discountType === "percentage" ? `(${discountValue}%)` : ""}
                                  </Typography>
                                  <Typography variant="body1" fontWeight={600} color="#ff9800">
                                    -€{discountAmount.toFixed(2)}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="body2" color="#555" fontWeight={600}>{t("subtotal_after_discount")}</Typography>
                                  <Typography variant="body1" fontWeight={600}>€{subtotalAfterDiscount.toFixed(2)}</Typography>
                                </Box>
                              </>
                            )}
                            
                            {!isTvaIncluded && (
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#e8f4f8', borderRadius: '4px' }}>
                                <Typography variant="body2" color="#0066cc" fontWeight={600}>{t("vat")} ({vatPercentage}%)</Typography>
                                <Typography variant="body1" fontWeight={600} color="#0066cc">€{vatAfterDiscount.toFixed(2)}</Typography>
                              </Box>
                            )}
                            
                            {outstandingTotal > 0 && (
                              <>
                                <Divider sx={{ my: 1 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="body2" color="#555" fontWeight={600}>{t("total")}</Typography>
                                  <Typography variant="body1" fontWeight={600}>€{grandTotal.toFixed(2)}</Typography>
                                </Box>
                              </>
                            )}

                            {outstandingTotal > 0 && (
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#fff8e1', borderRadius: '4px' }}>
                                <Typography variant="body2" color="#f57c00" fontWeight={600}>
                                  <ReceiptLongIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                  {t("outstanding_references") || "Outstanding"} ({selectedInvoices.length})
                                </Typography>
                                <Typography variant="body1" fontWeight={600} color="#f57c00">+€{outstandingTotal.toFixed(2)}</Typography>
                              </Box>
                            )}
                            
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              p: 2,
                              background: outstandingTotal > 0 ? 'linear-gradient(135deg, #e65100 0%, #f57c00 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              borderRadius: '6px',
                              mt: 1
                            }}>
                              <Typography variant="h6" fontWeight="700" color="white">{outstandingTotal > 0 ? (t("grand_total") || "Grand Total") : t("total")}</Typography>
                              <Typography variant="h5" fontWeight="700" color="white">€{(grandTotal + outstandingTotal).toFixed(2)}</Typography>
                            </Box>
                            
                            {/* Profit Display - Only when toggle is on and there are items */}
                            {showProfit && saleOrderProducts.length > 0 && (
                              <>
                                <Divider sx={{ my: 1 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#e8f5e9', borderRadius: '4px' }}>
                                  <Typography variant="body2" color="#2e7d32" fontWeight={600}>
                                    {t("total_cost")}
                                  </Typography>
                                  <Typography variant="body1" fontWeight={600} color="#2e7d32">
                                    €{totalCost.toFixed(2)}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#f1f8e9', borderRadius: '4px' }}>
                                  <Typography variant="body2" color="#558b2f" fontWeight={600}>
                                    {t("profit")} ({profitMargin.toFixed(1)}%)
                                  </Typography>
                                  <Typography variant="body1" fontWeight={700} color="#558b2f">
                                    €{totalProfit.toFixed(2)}
                                  </Typography>
                                </Box>
                              </>
                            )}
                          </Stack>
                        </Box>
                      </Box>

                  {/* Action Buttons */}
                  <Stack spacing={2}>
                    <Button
                      variant="outlined"
                      startIcon={<PreviewIcon />}
                      onClick={handlePreviewPdf}
                      fullWidth
                      disabled={!selectedCustomer || saleOrderProducts.length === 0}
                      sx={{
                        borderRadius: '8px',
                        py: 1.5,
                        borderColor: '#667eea',
                        color: '#667eea',
                        fontWeight: 600,
                        '&:hover': { borderColor: '#764ba2', bgcolor: alpha('#667eea', 0.05) }
                      }}
                    >
                      {t('preview_pdf')}
                    </Button>



                    {/* Reopen button for closed sale orders */}
                    {status === 'Closed' && (
                      <Button
                        variant="contained"
                        onClick={() => navigate(`/sales/sale-orders/${saleOrderId || saleOrderIdRef.current}`)}
                        fullWidth
                        disabled={loading}
                        sx={{ 
                          borderRadius: '8px',
                          py: 1.5,
                          bgcolor: '#2196f3', 
                          fontWeight: 600,
                          '&:hover': { bgcolor: '#1976d2' } 
                        }}
                      >
                        {t('view_details')}
                      </Button>
                    )}

                    {/* Draft/Close buttons only for non-closed orders */}
                    {status !== 'Closed' && (
                      <>
                        <Button
                          variant="contained"
                          startIcon={<DraftsIcon />}
                          onClick={handleSaveAsDraft}
                          fullWidth
                          disabled={loading || !selectedCustomer || saleOrderProducts.length === 0}
                          sx={{ 
                            borderRadius: '8px',
                            py: 1.5,
                            bgcolor: '#ff9800', 
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#f57c00' } 
                          }}
                        >
                          {t('save_as_draft')}
                        </Button>

                        <Button
                          variant="contained"
                          startIcon={<CheckCircleIcon />}
                          onClick={handleCloseSaleOrder}
                          fullWidth
                          disabled={loading || !selectedCustomer || saleOrderProducts.length === 0}
                          sx={{ 
                            borderRadius: '8px',
                            py: 1.5,
                            bgcolor: '#4caf50', 
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#388e3c' } 
                          }}
                        >
                          {t('close_sale_order')}
                        </Button>
                      </>
                    )}

                    <Button
                      variant="outlined"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => navigate('/sales/sale-orders')}
                      fullWidth
                      sx={{
                        borderRadius: '8px',
                        py: 1.5,
                        fontWeight: 600,
                      }}
                    >
                      {t('back')}
                    </Button>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>

            {/* General Errors Display */}
            {generalErrors.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Alert severity="error">
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    {t("please_fix_following_errors") || "Please fix the following errors:"}
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {generalErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </Alert>
              </Box>
            )}
          </Box>
        </Paper>
      </Container>

      {/* Dialogs */}
      <AddCustomerDialog
        open={openCustomerDialog}
        onClose={() => setOpenCustomerDialog(false)}
        onCustomerCreated={handleNewCustomer}
        optionalFields={{ vat: true, companyName: true, email: true, phone: true }}
        hidePeppol={true}
      />
      <AddCustomerDialog
        open={openEditCustomerDialog}
        onClose={() => setOpenEditCustomerDialog(false)}
        editMode={true}
        initialData={selectedCustomer}
        onCustomerUpdated={(customer) => { setSelectedCustomer(customer); }}
        optionalFields={{ vat: true, companyName: true, email: true, phone: true }}
        hidePeppol={true}
      />

      <AddProductDialog
        open={openProductDialog}
        onClose={() => {
          setOpenProductDialog(false);
          setProductSearchText(''); // Clear search text when closing
        }}
        onProductAdded={handleNewProduct}
        initialName={productSearchText}
      />

      <InvoicePreviewDialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        html={previewHtml}
        documentTitle={`${t('sale_order')} ${saleOrderNumber}`}
      />

      <ConfirmationDialog
        open={draftConfirmDialog}
        title={t('save_as_draft')}
        message={t('confirm_save_draft_message')}
        onConfirm={confirmSaveAsDraft}
        onCancel={() => setDraftConfirmDialog(false)}
        confirmText={t('save')}
        cancelText={t('cancel')}
        variant="warning"
        loading={loading}
      />

      <ConfirmationDialog
        open={saveConfirmDialog}
        title={t('close_sale_order')}
        message={
          <Box>
            <Typography variant="body1" fontWeight={600} gutterBottom>
              {t('confirm_close_sale_order_summary')}
            </Typography>
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{t('sale_order_number')}:</Typography>
                  <Typography variant="body2" fontWeight={600}>{saleOrderNumber}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{t('number_of_items')}:</Typography>
                  <Typography variant="body2" fontWeight={600}>{saleOrderProducts.length}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{t('total_amount')}:</Typography>
                  <Typography variant="body2" fontWeight={600} color="primary">€{grandTotal.toFixed(2)}</Typography>
                </Grid>
                {discountValue > 0 && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">{t('discount')}:</Typography>
                    <Typography variant="body2" fontWeight={600} color="warning.main">
                      {discountType === 'percentage' ? `${discountValue}%` : `€${discountValue}`}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {t('are_you_sure_close_sale_order')}
            </Typography>
          </Box>
        }
        onConfirm={confirmCloseSaleOrder}
        onCancel={() => setSaveConfirmDialog(false)}
        confirmText={t('confirm_close')}
        cancelText={t('edit')}
        variant="success"
        loading={loading}
      />

      {/* Stock Warning Dialog */}
      {stockWarningData && (
        <ConfirmationDialog
          open={stockWarningDialog}
          title={t('insufficient_stock') || 'Insufficient Stock'}
          message={
            <Box>
              <Typography variant="body1" gutterBottom>
                {t('requested_quantity_exceeds_stock') || 'The requested quantity exceeds available stock.'}
              </Typography>
              <Box sx={{ mt: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>{t('product')}:</strong> {stockWarningData.product.Name || stockWarningData.product.ProductName}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>{t('requested_quantity')}:</strong> {stockWarningData.requestedQuantity}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, color: 'error.main' }}>
                  <strong>{t('available_stock')}:</strong> {stockWarningData.stockQuantity}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, color: 'warning.main' }}>
                  <strong>{t('shortage')}:</strong> {stockWarningData.requestedQuantity - stockWarningData.stockQuantity}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {t('click_confirm_to_continue') || 'Click Confirm to continue with the requested quantity anyway.'}
              </Typography>
            </Box>
          }
          onConfirm={handleContinueWithStock}
          confirmText={t('continue_anyway') || 'Continue Anyway'}
          autoFocusConfirm={true}
          onCancel={() => {
            setStockWarningDialog(false);
            setStockWarningData(null);
          }}
          cancelText={t('cancel') || 'Cancel'}
          variant="warning"
        />
      )}
    </Box>
  );
}

export default AddSaleOrderPage;
